import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useCallback, RefObject, useRef } from "react";
import { useSearchContext } from "../../../components/Header/SearchContext";

type Props = {
  containerRef: RefObject<HTMLDivElement | null>;
};

export function useHeaderSearch({ containerRef }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { query, setQuery, clear, isOpen, setIsOpen } = useSearchContext();
  const isHomePage = location.pathname === "/" || location.pathname === "/home";
  const handleClearSearch = useCallback(() => {
    clear();
  }, [clear]);

  const handleSearchToggle = useCallback(() => {
    if (!isOpen) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });

      setTimeout(() => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 100);

      setIsOpen(true);
      return;
    }

    handleClearSearch();
  }, [isOpen, setIsOpen, handleClearSearch]);

  const handleSearchEnter = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const handleSearchChange = useCallback(
    (value: string) => {
      if (!query && value && !isHomePage) {
        navigate("/home");
      }

      setQuery(value);
    },
    [query, setQuery, navigate, isHomePage],
  );

  useEffect(() => {
    if (!isOpen) return;
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (!isMobile) return;

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isHomePage) return;

      if (e.ctrlKey && e.key === "k") {
        e.preventDefault();
        handleSearchToggle();
      }

      if (e.key === "Escape" && isOpen) {
        handleClearSearch();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isHomePage, handleSearchToggle, handleClearSearch]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen, handleClearSearch, containerRef, setIsOpen]);

  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const prevPath = prevPathRef.current;
    const currentPath = location.pathname;
    if (
      (prevPath === "/" || prevPath === "/home") &&
      !(currentPath === "/" || currentPath === "/home")
    ) {
      setIsOpen(false);
      clear();
    }

    prevPathRef.current = currentPath;
  }, [location.pathname, setIsOpen, clear]);

  return {
    query,
    isOpen,
    handleSearchToggle,
    handleSearchEnter,
    handleSearchChange,
    handleClearSearch,
  };
}
